import type { IEventResultScore, IMatch } from '@/interface/IMatch';
import type { IBetBuilderMarket } from '@/interface/betBuilder';
import type { BetTypeGroup } from '@/interface/enum';
import { BetBuilderDisplayType, BetBuilderMarket, BetBuilderSelectionType, BetBuilderStatType } from '@/interface/enum';
import { i18n } from '@/libraries/i18n';
import { getBetBuilderScoreGroup, hasBetBuilderScoreGroup } from '@/libraries/oddsApi/betBuilderScoreSource';

const marketExpectedLength: Partial<Record<BetBuilderMarket | string, number>> = {
    [BetBuilderMarket.SetHandicap]: 2,
    [BetBuilderMarket.SetTotalGoalsOverUnder]: 2,
    [BetBuilderMarket.Set1x2]: 1,
};

const shortStatTypeName: Partial<Record<BetBuilderStatType, string>> = {
    [BetBuilderStatType.FullTime]: 'marketType_FT_Name',
    [BetBuilderStatType.FirstHalf]: 'marketType_HT_Name',
    [BetBuilderStatType.SecondHalf]: 'oddsDisplay_matchInfo_secondHalf',
    [BetBuilderStatType.Unknown]: '',
};

export function getColumnCount(marketType: BetBuilderMarket | string) {
    return marketExpectedLength[marketType] ?? 1;
}

export function displayStatTypeName(statType: BetBuilderStatType) {
    return i18n.t(shortStatTypeName[statType] ?? '').toString() || '';
}

export function isUseShortNameType(selectionTypeId: number) {
    const usingShortNameType = [
        BetBuilderSelectionType.Over,
        BetBuilderSelectionType.Under,
        BetBuilderSelectionType.Odd,
        BetBuilderSelectionType.Even,
        BetBuilderSelectionType._1,
        BetBuilderSelectionType._2,
        BetBuilderSelectionType.X,
        BetBuilderSelectionType._1X,
        BetBuilderSelectionType._12,
        BetBuilderSelectionType._2X,
        BetBuilderSelectionType.Home,
        BetBuilderSelectionType.Away,
    ];

    return usingShortNameType.includes(selectionTypeId);
}

export function isShowPointDisplayType(displayType: BetBuilderDisplayType) {
    const showPointDisplay = [
        BetBuilderDisplayType.Handicap,
        BetBuilderDisplayType.OverUnder,
        BetBuilderDisplayType.FH_Handicap,
        BetBuilderDisplayType.FH_OverUnder,
    ];

    return showPointDisplay.includes(displayType);
}

export function isHandicap(displayType: BetBuilderDisplayType) {
    const showPointDisplay = [
        BetBuilderDisplayType.Handicap,
        BetBuilderDisplayType.FH_Handicap,
    ];

    return showPointDisplay.includes(displayType);
}

export function isAwayTeam(selectionType: BetBuilderSelectionType) {
    return selectionType === BetBuilderSelectionType.Away;
}

export function isShowPointMarketType(marketType: BetBuilderMarket) {
    const showPointMarket = [
        BetBuilderMarket.SetTotalGoalsOverUnder,
        BetBuilderMarket.SetHandicap,
    ];

    return showPointMarket.includes(marketType);
}

export function getEventResultScore(eventResults: IEventResultScore[], marketGroupId: BetTypeGroup): IEventResultScore | null {
    const score = eventResults.find(eventResult => eventResult.marketGroupId === marketGroupId);
    if (!score || score.liveHomeScore < 0 || score.liveAwayScore < 0) {
        return null;
    }

    return score;
}

export function formatMarketGroupScore(score: IEventResultScore | null): string {
    return score ? `[${score.liveHomeScore}:${score.liveAwayScore}]` : '';
}

export function getMarketGroupScore(match: IMatch, markets: IBetBuilderMarket[]): IEventResultScore | null {
    if (!match.isLive) {
        return null;
    }

    const scoreGroup = markets
        .map(market => getBetBuilderScoreGroup(market.marketType.id))
        .find(group => group !== null) ?? null;

    return scoreGroup !== null ? getEventResultScore(match.eventResults, scoreGroup) : null;
}

export function getBetBuilderTicketGroupScore(
    isLive: boolean,
    marketType: BetBuilderMarket,
    eventResults: IEventResultScore[],
): IEventResultScore | null {
    const scoreGroup = getBetBuilderScoreGroup(marketType);
    if (!isLive || scoreGroup === null) {
        return null;
    }

    return getEventResultScore(eventResults, scoreGroup);
}

export function isShowBetBuilderSlipMainScore(isLive: boolean, marketTypes: BetBuilderMarket[]): boolean {
    return isLive && marketTypes.some(marketType => !hasBetBuilderScoreGroup(marketType));
}

// A bet builder bet carries no bet-level live score: every leg that settles against a score
// brings its own betLiveScore — handicap legs the match score, legs with their own score group
// (e.g. total corner) the group score.
export function isShowBetBuilderLegMatchScore(isLive: boolean, marketType: BetBuilderMarket): boolean {
    return isLive && marketType === BetBuilderMarket.SetHandicap;
}

export function isShowBetBuilderLegGroupScore(isLive: boolean, marketType: BetBuilderMarket): boolean {
    return isLive && hasBetBuilderScoreGroup(marketType);
}
